import { PACK_SIZE, type Question } from './pack';
import { selectWords } from './quiz';
import { cursor, nextInt, takeK } from './rng';
import type { EngineRules } from './rules';
import type { EngineCtx, EngineState, ReviveView } from './types';

/**
 * 부활 문제·선택지 (RULES §1.14 · ADR-032).
 *
 * PRNG 소비 순서 고정: 문제 → 오답 → 정답 위치. 폴백 여부와 무관하게 draw 수가 같으므로 결정론 무손상.
 * 후보 집합은 사망 시점(플레이어 입력)에 의존하지 않는다 — 이 판 8제 전체와 세션 기출을 뺀다.
 */
export interface ReviveQuiz {
  readonly id: string;
  readonly choices: readonly string[];
  readonly correctIndex: number;
}

export interface ReviveDraw {
  readonly words: readonly string[]; // 이 판의 8제
  readonly sessionSeed: string;
  readonly runIndex: number;
  readonly rngRevive: number;
}

export function pickReviveQuiz(d: ReviveDraw, ctx: EngineCtx): { quiz: ReviveQuiz; rng: number } {
  const R = ctx.rules;
  const rc = cursor(d.rngRevive);

  // (1) 문제 — 이 판 제외 + 세션 기출 제외. 비면 가장 오래된 판만 남기는 폴백(24제 = 3판 소진이라 runIndex 2 부터 발동)
  const inRun = new Set<string>(d.words);
  const seenByRun: string[][] = [];
  for (let r = 0; r < d.runIndex; r++) {
    seenByRun.push([...selectWords(ctx.ordered, d.sessionSeed, r, R.wordLenProfile)]);
  }
  const seen = new Set<string>();
  for (const ws of seenByRun) for (const w of ws) seen.add(w);
  let pool = ctx.ordered.filter((q) => !inRun.has(q.id) && !seen.has(q.id));
  if (pool.length === 0) {
    const oldest = new Set<string>(seenByRun[0] ?? []);
    pool = ctx.ordered.filter((q) => !inRun.has(q.id) && oldest.has(q.id));
  }
  if (pool.length === 0)
    throw new Error('pickReviveQuiz: 후보 없음 — assertRevivePool 이 먼저 막았어야 한다');
  const q = pool[nextInt(rc, pool.length)] as Question;

  // (2) 오답 — 같은 글자 수의 실재 정답. 사다리 변형어는 뜻이 없어 "말이 되는 것"을 고르면 맞는다(설계원칙 2 위반)
  const src = ctx.ordered
    .filter((x) => x.a.length === q.a.length && x.id !== q.id && !inRun.has(x.id))
    .map((x) => x.a);
  const wrongs = takeK(rc, src, R.reviveChoiceCount - 1);
  if (wrongs.length < R.reviveChoiceCount - 1) throw new Error('pickReviveQuiz: 오답 후보 부족');

  // (3) 정답 위치
  const ci = nextInt(rc, R.reviveChoiceCount);
  const choices = [...wrongs];
  choices.splice(ci, 0, q.a);
  return { quiz: { id: q.id, choices, correctIndex: ci }, rng: rc.s };
}

/** 부팅 어서션(RULES §1.14) — 팩·룰 조합에서 부활 문제와 오답이 항상 나오는가. 정답 전역 유일도 여기서 본다. */
export function assertRevivePool(ctx: EngineCtx): void {
  const R = ctx.rules;
  const fail = (m: string): never => {
    throw new Error(`RULES 위반(부활): ${m}`);
  };
  if (!R.reviveEnabled) return;
  if (PACK_SIZE - R.runWordCount < 1) fail('PACK_SIZE − runWordCount < 1');
  const answers = new Set<string>();
  for (const q of ctx.ordered) {
    if (answers.has(q.a)) fail(`정답 중복 '${q.a}' — 선택지에 같은 문자열이 두 번 나온다`);
    answers.add(q.a);
  }
  const byLen = new Map<number, number>();
  for (const q of ctx.ordered) byLen.set(q.a.length, (byLen.get(q.a.length) ?? 0) + 1);
  const inProfile = new Map<number, number>();
  for (const L of R.wordLenProfile) inProfile.set(L, (inProfile.get(L) ?? 0) + 1);
  for (const [L, n] of byLen) {
    const avail = n - 1 - (inProfile.get(L) ?? 0);
    if (avail < R.reviveChoiceCount - 1)
      fail(`길이 ${L}: 오답 후보 ${avail} < ${R.reviveChoiceCount - 1}`);
  }
}

/** 뷰 투영 — 판정 전에는 정답 위치가 담기지 않는다(ADR-008 확장). */
export function toReviveView(s: EngineState, q: Question, rules: EngineRules): ReviveView {
  const rv = s.revive;
  return {
    status: rv.status,
    cause: rv.cause,
    question: q.q,
    choices: rv.choices,
    picked: rv.picked,
    revealIndex: rv.status >= 2 ? rv.correctIndex : -1,
    armed: rv.armTicksLeft === 0,
    ticksLeft: rv.ticksLeft,
    limitTicks: rules.reviveLimitTicks,
  };
}
